import { Accordion, Button, FilterIcon } from "@au-schein-de/au-ui-library";
import type { FC } from "react";
import { useTranslation } from "react-i18next";
import { FiltersProvider } from "./filters.context";
import {
	handleFilterTriggerClick,
	handleFilterTriggerKeyDown,
} from "./scroll-filter-into-view";
import { ComplaintsFilter } from "./sections/complaints-filter.component";
import { DeliveryVendorFilter } from "./sections/delivery-vendor-filter.component";
import { EffectsFilter } from "./sections/effects-filter.component";
import { FlavorsFilter } from "./sections/flavors-filter.component";
import { GeneticsFilter } from "./sections/genetics-filter.component";
import { IrradiationFilter } from "./sections/irradiation-filter.component";
import { PriceFilter } from "./sections/price-filter.component";
import { ProducersFilter } from "./sections/producer-filter.component";
import { ProductCategoriesPills } from "./sections/product-categories-pills.component";
import { RatingFilter } from "./sections/rating-filter.component";
import { TerpenesFilter } from "./sections/terpenes-filter.component";
import { ThcCbdFilter } from "./sections/thc-cbd-filter.component";
import { TopProducersLowPriceFilter } from "./sections/top-producers-low-price-filter.component";
import type {
	FacetCounts,
	FilterSetter,
	FiltersConfig,
	FiltersState,
} from "./types";
import { getActiveFilterGroupsCount } from "./utils";

interface ProductFiltersProps {
	filters: FiltersState;
	config: FiltersConfig;
	onFilterChange: FilterSetter;
	disableTopProducersFilter?: boolean;
	onClearAll?: () => void;
	facets?: FacetCounts;
	onTopProducerWarning?: () => void;
}

export const ProductFilters: FC<ProductFiltersProps> = ({
	filters,
	config,
	onFilterChange,
	disableTopProducersFilter = false,
	onClearAll,
	facets,
	onTopProducerWarning,
}) => {
	const { t } = useTranslation("product");

	const activeFiltersCount = getActiveFilterGroupsCount(filters);

	return (
		<FiltersProvider
			filters={filters}
			config={config}
			onFilterChange={onFilterChange}
			disableTopProducersFilter={disableTopProducersFilter}
			onClearAll={onClearAll}
			facets={facets}
			onTopProducerWarning={onTopProducerWarning}
		>
			<aside
				className="max-h-[85vh] md:overflow-auto px-1 -mx-1"
				onClick={handleFilterTriggerClick}
				onKeyDown={handleFilterTriggerKeyDown}
			>
				<div>
					<div className="hidden md:block mb-8">
						<DeliveryVendorFilter />
					</div>
					{/* The tiles row above the listing is the only desktop category
					    entry point; on mobile the pills live in the filter drawer. */}
					<div className="mb-6 md:hidden">
						<ProductCategoriesPills />
					</div>
					<div className="hidden md:flex justify-between items-center mb-4">
						<div className="flex items-center gap-2">
							<div className="text-ansay-main">
								<FilterIcon size={5} />
							</div>
							<h2 className="text-body">
								{t("filters")}{" "}
								{activeFiltersCount > 0 && `(${activeFiltersCount})`}
							</h2>
						</div>
						{activeFiltersCount > 0 && onClearAll && (
							<Button variant="link" size="fit" onClick={onClearAll}>
								{t("filters.clearAll")}
							</Button>
						)}
					</div>
					<Accordion.Root
						type="multiple"
						className="space-y-2"
						defaultValue={[]}
					>
						<ComplaintsFilter />

						<ProducersFilter>
							{!disableTopProducersFilter && <TopProducersLowPriceFilter />}
						</ProducersFilter>

						<PriceFilter />

						<RatingFilter />

						<GeneticsFilter />

						<ThcCbdFilter />

						<EffectsFilter />

						<FlavorsFilter />

						<TerpenesFilter />

						<IrradiationFilter />
					</Accordion.Root>
				</div>
			</aside>
		</FiltersProvider>
	);
};
